import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import {
  getActiveMember,
  setActiveMember as setStore,
  type ActiveMember,
} from '../lib/active-member';

interface ActiveMemberContextValue {
  /** The shared member currently being acted as, or null to act as yourself. */
  activeMemberId: string | null;
  /** The shared user's household — the app is scoped to it while acting as them. */
  activeMemberHouseholdId: string | null;
  setActiveMember: (member: ActiveMember | null) => void;
}

const ActiveMemberContext = createContext<ActiveMemberContextValue>({
  activeMemberId: null,
  activeMemberHouseholdId: null,
  setActiveMember: () => {},
});

export function ActiveMemberProvider({ children }: { children: ReactNode }) {
  const [active, setState] = useState<ActiveMember | null>(() => getActiveMember());

  const setActiveMember = useCallback((member: ActiveMember | null) => {
    setStore(member); // keep the module store + localStorage in sync for the api-client
    setState(member);
  }, []);

  return (
    <ActiveMemberContext.Provider
      value={{
        activeMemberId: active?.id ?? null,
        activeMemberHouseholdId: active?.householdId ?? null,
        setActiveMember,
      }}
    >
      {children}
    </ActiveMemberContext.Provider>
  );
}

export function useActiveMemberContext() {
  return useContext(ActiveMemberContext);
}
